import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import i18n from 'i18n-js';
import moment from 'moment';

import { scale } from '@components/frame';
import { Alert } from '@helpers';

import ColorWrapper from '@styles/colors/index';
import { font } from '@styles/fonts';
import Button from '@styles/buttons';

import Header from '@components/settings/v2/common/SettingsHeader';
import LineButton from '@components/common/lineButton';
import Loading from '@components/loading';
import {
  BACK_BUTTON_SETTINGS_TEXT,
  modalFieldBg,
  SETTINGS_HEADER_MARGIN_BOTTOM_WITH_SHADOW_OFFSET,
  SETTINGS_ITEM_SHADOW_OFFSET,
  TEST_IDS,
} from '../../../../../v2/pages/common/constants/base.constants';
import { getBoxShadowStyle } from '@styles/shadow';
import { useManageAPIKeys } from './hooks/useManageAPIKeys';

const Key = (props) => {
  const { C, keyLoading, apiKeys, generatedApiKey, generateKeys, keyNum } = props;

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  const key = apiKeys.find((key) => key.sequenceNumber === keyNum) || generatedApiKey || {};
  const keyHint = key.keyHint;
  const keyAuthor = key.generatedBy;
  const keyDate = moment(key.generatedOn).format('DD MMMM YYYY');

  const copy = () => {
    Clipboard.setString(generatedApiKey.key);
    Alert({
      title: i18n.t('Copied to clipboard'),
      type: 'success',
    });
  };

  return (
    <View
      testID={keyNum === 1 ? TEST_IDS.apiKeysScreen.keyCard1 : TEST_IDS.apiKeysScreen.keyCard2}
      style={{
        backgroundColor: C(modalFieldBg),
        paddingHorizontal: scale(15),
        paddingVertical: scale(18),
        borderRadius: scale(10),
        marginBottom: scale(20),
        ...shadowStyles,
      }}
    >
      <View style={{ flexDirection: 'row', marginBottom: scale(12) }}>
        <Text style={[font['Title 2'], { color: C('Label / Light / Primary'), marginRight: 'auto' }]}>{i18n.t(`Key ${keyNum}`)}</Text>
        <View style={{ flexDirection: 'row', gap: scale(15) }}>
          {generatedApiKey?.key && (
            <Button
              {...{
                variant: 'S',
                type: 'Primary',
                borderRadius: scale(15),
                onPress: copy,
              }}
            >
              {i18n.t('Copy')}
            </Button>
          )}
          <Button
            {...{
              variant: 'S',
              type: 'Primary',
              borderRadius: scale(15),
              loading: keyLoading === keyNum,
              disabled: keyLoading && keyLoading !== keyNum,
              onPress: () => generateKeys([keyNum]),
            }}
          >
            {i18n.t('Regenerate')}
          </Button>
        </View>
      </View>
      <View
        style={{
          backgroundColor: C('Background/Light/Field lighter'),
          paddingHorizontal: scale(15),
          paddingVertical: scale(8),
          borderRadius: scale(10),
          marginBottom: scale(6),
        }}
      >
        <Text style={[font['Footnote'], { color: C('Label / Light / Primary'), marginRight: 'auto' }]}>
          {generatedApiKey?.key || `${keyHint}************`}
        </Text>
      </View>
      <Text style={[font['Footnote'], { color: C('Label / Light / Tertiary') }]}>
        {i18n.t(`Created by {{keyAuthor}} on {{keyDate}}`, { keyAuthor, keyDate })}
      </Text>
    </View>
  );
};

const NoAPIKeys = (props) => {
  const { C, isLoading, generateKeys } = props;

  if (isLoading) {
    return (
      <View style={{ width: '100%', flex: 1 }}>
        <Loading background={C('Background/Light/Detail')} />
      </View>
    );
  }

  return (
    <View testID={TEST_IDS.apiKeysScreen.emptyState}>
      <Header backBtnText={BACK_BUTTON_SETTINGS_TEXT} key={'APIKeysHeader'} />
      <View
        style={[
          {
            width: '100%',
            height: '100%',
            backgroundColor: C('Background/Light/Detail'),
            alignItems: 'center',
            paddingHorizontal: scale(29),
            marginTop: scale(132),
          },
        ]}
      >
        <View
          style={{
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <View style={{ paddingLeft: scale(15), marginBottom: scale(15) }}>
            <Text style={[font['Title 2'], { color: C('Label / Light / Primary') }]}>{i18n.t('No API Keys')}</Text>
          </View>
          <View style={{ paddingLeft: scale(15), marginBottom: scale(61) }}>
            <Text style={[font['Footnote'], { color: C('Label / Light / Primary'), textAlign: 'center' }]}>
              {i18n.t('Generate an API Key to get data out of Smart Factory and to integrate with other systems.')}
            </Text>
          </View>
          <View testID={TEST_IDS.apiKeysScreen.generateButton} style={{ width: scale(300) }}>
            <Button {...{ variant: 'Dialog L', type: 'Primary', borderRadius: scale(10), onPress: () => generateKeys([1, 2]) }}>
              {i18n.t('Generate API Key')}
            </Button>
          </View>
        </View>
      </View>
    </View>
  );
};

const APIKeys = (props) => {
  const { C } = props;

  const { apiKeys, generatedApiKey1, generatedApiKey2, isLoading, keyLoading, hasKeys, generateKeys, deleteKeys } = useManageAPIKeys();

  if (!hasKeys) {
    return <NoAPIKeys {...{ C, isLoading, generateKeys }} />;
  }

  return [
    <View key="APIKeys" style={{ width: '100%', height: '100%', backgroundColor: C('Background/Light/Detail'), alignItems: 'center' }}>
      <Header
        backBtnText={BACK_BUTTON_SETTINGS_TEXT}
        style={{ marginBottom: scale(SETTINGS_HEADER_MARGIN_BOTTOM_WITH_SHADOW_OFFSET) }}
        name={'API Keys'}
        key={'APIKeysHeader'}
      />
      <ScrollView
        style={{
          width: '100%',
          paddingHorizontal: scale(18),
          paddingTop: scale(SETTINGS_ITEM_SHADOW_OFFSET),
        }}
      >
        <Key {...{ C, keyLoading, apiKeys, generatedApiKey: generatedApiKey1, generateKeys, keyNum: 1 }} />
        <Key {...{ C, keyLoading, apiKeys, generatedApiKey: generatedApiKey2, generateKeys, keyNum: 2 }} />
        <View style={{ marginTop: scale(4), paddingHorizontal: scale(15) }}>
          <Text style={[font['Footnote'], { color: C('Label / Light / Primary'), marginBottom: scale(24) }]}>
            {i18n.t('We provide you with 2 API Keys to allow you to regenerate one while still using the other.')}
          </Text>
          <Text style={[font['Footnote'], { color: C('Label / Light / Primary'), marginBottom: scale(24) }]}>
            {i18n.t(
              'Keys will only be shown once and will never be shown to you again. As a security precaution, copy and paste it into your application or script and do not save a copy.'
            )}
          </Text>
          <Text style={[font['Footnote'], { color: C('Label / Light / Primary'), marginBottom: scale(24) }]}>
            {i18n.t('The API Key can be used for endpoint: https://api.smarfac.com')}
          </Text>
        </View>
        <LineButton
          {...{
            C,
            onPress: deleteKeys,
            loading: keyLoading === 3,
            text: i18n.t('Delete'),
            color: 'Label / Light / Error',
            testID: TEST_IDS.apiKeysScreen.deleteButton,
          }}
        />
      </ScrollView>
    </View>,
  ];
};

export default ColorWrapper(APIKeys);
